interface OpsJobRun {
  id: string
  time: string
  job_type: string
  property_id: string
  property_name: string
  status: string
  duration_ms: number
  error_snippet: string
  retryable: boolean
}

export function OpsJobRunsCard({
  rows,
  loading,
  failedOnly,
  type,
  typeOptions,
  retryingJobId,
  onFailedOnlyChange,
  onTypeChange,
  onRetry,
  onOpenProperty,
}: {
  rows: OpsJobRun[]
  loading: boolean
  failedOnly: boolean
  type: string
  typeOptions: string[]
  retryingJobId: string
  onFailedOnlyChange: (value: boolean) => void
  onTypeChange: (value: string) => void
  onRetry: (jobId: string) => void
  onOpenProperty: (propertyId: string) => void
}) {
  return (
    <div className="section-card" style={{ padding: 0, overflow: "hidden" }}>
      <div className="ops-card-header">
        <div className="section-card__title" style={{ marginBottom: 0 }}>Jobs & Failures</div>
        <div className="ops-chip-row">
          <button
            type="button"
            className={`ops-filter-chip${!failedOnly ? " ops-filter-chip--active" : ""}`}
            onClick={() => onFailedOnlyChange(false)}
          >
            All
          </button>
          <button
            type="button"
            className={`ops-filter-chip${failedOnly ? " ops-filter-chip--active" : ""}`}
            onClick={() => onFailedOnlyChange(true)}
          >
            Failed only
          </button>
          <select
            className="audit-filters__select"
            value={type}
            onChange={(event) => onTypeChange(event.target.value)}
            style={{ minWidth: 160 }}
          >
            <option value="">All job types</option>
            {typeOptions.map((jobType) => (
              <option key={jobType} value={jobType}>{jobType}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: "var(--sf-space-8)", textAlign: "center" }}>
          <div className="sf-spinner" style={{ margin: "0 auto" }} />
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__title">No job runs</div>
        </div>
      ) : (
        <div className="data-table-wrapper">
          <table className="data-table data-table--compact">
            <thead>
              <tr>
                <th>Time</th>
                <th>Job Type</th>
                <th>Property</th>
                <th>Status</th>
                <th>Duration</th>
                <th>Error</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{formatTime(row.time)}</td>
                  <td>{row.job_type || "—"}</td>
                  <td>
                    {row.property_id ? (
                      <button
                        type="button"
                        className="data-table__action"
                        onClick={() => onOpenProperty(row.property_id)}
                      >
                        {row.property_name || row.property_id.slice(0, 8)}
                      </button>
                    ) : (
                      "System"
                    )}
                  </td>
                  <td>
                    <span className={`status-pill status-pill--${statusClass(row.status)}`}>
                      <span className="status-pill__dot" />
                      {row.status || "unknown"}
                    </span>
                  </td>
                  <td>{formatDuration(row.duration_ms)}</td>
                  <td className="ops-error-snippet">{row.error_snippet || "—"}</td>
                  <td>
                    {row.retryable && row.status === "failed" ? (
                      <button
                        type="button"
                        className="data-table__action"
                        onClick={() => onRetry(row.id)}
                        disabled={retryingJobId === row.id}
                      >
                        {retryingJobId === row.id ? "Retrying..." : "Retry"}
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function formatTime(value: string) {
  if (!value) return "—"
  const ts = Date.parse(value)
  if (!Number.isFinite(ts)) return value
  return new Date(ts).toLocaleString()
}

function formatDuration(ms: number) {
  const value = Number(ms) || 0
  if (value <= 0) return "—"
  if (value < 1000) return `${value}ms`
  return `${(value / 1000).toFixed(1)}s`
}

function statusClass(status: string) {
  const normalized = (status || "").toLowerCase()
  if (normalized === "completed" || normalized === "ok") return "healthy"
  if (normalized === "failed" || normalized === "error") return "error"
  if (normalized === "running") return "warning"
  return "neutral"
}
